import React, { useEffect, useState } from 'react';
import { AiFillInfoCircle } from 'react-icons/ai';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import AsyncTable, { Column } from '../../../../components/AsyncTable';
import { ActionButtonsContainer } from '../../../../components/TablePaginator/styles';
import {
  getLocalizedHelpCenterMeta,
  HelpCenterModal,
} from '../../../../pages/HelpCenter';
import {
  DATABASE_MANAGEMENT_FILE_GUIDE_SLUG,
} from '../../../../pages/HelpCenter/content/databaseManagementFileGuide';
import {
  VISIT_SCHEDULE_CSV_GUIDE_SLUG,
} from '../../../../pages/HelpCenter/content/visitRoutinesCsvGuide';
import { getHelpCenterContentBySlug } from '../../../../pages/HelpCenter/registry';
import { FileUploadIcon, DownloadIcon, PreviewIcon, HelpIcon } from '../../../../icons';
import {
  databaseManagementAvailableTablesApi,
} from '../../../../services/requests';
import ContentContainer from '../../../../templates/Content/ContentContainer';
import { getGenericRequestPageForAsyncTable } from '../../../../utils/TablesFunctions';
import DownloadLatestExportModal from './components/DownloadLatestExportModal';
import UpdateErrorDetailsModal from './components/UpdateErrorDetailsModal';
import UpdateFromFileModal from './components/UpdateFromFileModal';

export type DatabaseManagementTable = {
  label: string;
  value: string;
  updateType?: {
    incremental: boolean;
    completeReplacement: boolean;
  };
  updateStatus?: string | null;
  updateErrorMessage?: string | null;
  updateJobHistoryId?: number | null;
  updateRequestedAt?: string | null;
};

const VISIT_SCHEDULE_TABLE_VALUES = new Set(['visita_agendamento', 'visit_routines']);

const isVisitScheduleTable = (row: DatabaseManagementTable): boolean => (
  row.label === 'visitSchedule'
  || row.label === 'visitRoutines'
  || VISIT_SCHEDULE_TABLE_VALUES.has(row.value)
);

const DatabaseManagementPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  const [tableData, setTableData] = useState<DatabaseManagementTable[]>([]);
  const [updateTable, setUpdateTable] = useState(0);

  const [selectedTable, setSelectedTable] = useState<DatabaseManagementTable | null>(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [selectedErrorMessage, setSelectedErrorMessage] = useState('');
  const [activeHelpSlug, setActiveHelpSlug] = useState<string | null>(null);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setUpdateTable((currentValue) => currentValue + 1);
    }, 30000);

    return () => window.clearInterval(interval);
  }, []);

  const closeUpdateModal = () => {
    setShowUpdateModal(false);
    setSelectedTable(null);
  };

  const closeDownloadModal = () => {
    setShowDownloadModal(false);
    setSelectedTable(null);
  };

  const closeErrorModal = () => {
    setShowErrorModal(false);
    setSelectedErrorMessage('');
    setSelectedTable(null);
  };

  const openUpdateModal = (row: DatabaseManagementTable) => {
    setSelectedTable(row);
    setShowUpdateModal(true);
  };

  const openDownloadModal = (row: DatabaseManagementTable) => {
    setSelectedTable(row);
    setShowDownloadModal(true);
  };

  const openErrorModal = (row: DatabaseManagementTable) => {
    setSelectedTable(row);
    setSelectedErrorMessage(String(row.updateErrorMessage ?? ''));
    setShowErrorModal(true);
  };

  const refreshTableStatuses = () => {
    setUpdateTable((currentValue) => currentValue + 1);
  };

  const generalHelpContent = getHelpCenterContentBySlug(
    DATABASE_MANAGEMENT_FILE_GUIDE_SLUG,
    'modal',
  );
  const visitScheduleHelpContent = getHelpCenterContentBySlug(
    VISIT_SCHEDULE_CSV_GUIDE_SLUG,
    'modal',
  );
  const generalHelpTitle = generalHelpContent
    ? getLocalizedHelpCenterMeta(generalHelpContent, i18n.resolvedLanguage ?? i18n.language).title
    : null;
  const visitScheduleHelpTitle = visitScheduleHelpContent
    ? getLocalizedHelpCenterMeta(
      visitScheduleHelpContent,
      i18n.resolvedLanguage ?? i18n.language,
    ).title
    : null;

  const openHelpModal = (slug: string) => {
    setActiveHelpSlug(slug);
  };

  const closeHelpModal = () => {
    setActiveHelpSlug(null);
  };

  const handleStatusColumn = (row: DatabaseManagementTable) => {
    switch (row.updateStatus) {
      case 'error':
        return <p style={{ color: theme.colors.negative }}>{t('error')}</p>;
      case 'success':
        return <p style={{ color: theme.colors.positive }}>{t('success')}</p>;
      case 'processing':
        return <p style={{ color: theme.colors.warning }}>{t('processing')}</p>;
      case 'inQueue':
        return <p>{t('inQueue')}</p>;
      default:
        return <span />;
    }
  };

  const actionsButtons = (row: DatabaseManagementTable) => (
    <ActionButtonsContainer type={5}>
      {row.updateStatus === 'error' && row.updateErrorMessage ? (
        <PreviewIcon
          title={t('viewTheError')}
          onClick={() => openErrorModal(row)}
        />
      ) : null}
      <DownloadIcon
        title={t('downloadLatestExport')}
        onClick={() => openDownloadModal(row)}
      />
      <FileUploadIcon title={t('updateFromFile')} onClick={() => openUpdateModal(row)} />
      {isVisitScheduleTable(row) && visitScheduleHelpContent ? (
        <HelpIcon
          title={visitScheduleHelpTitle ?? undefined}
          onClick={() => openHelpModal(VISIT_SCHEDULE_CSV_GUIDE_SLUG)}
        />
      ) : null}
    </ActionButtonsContainer>
  );

  const columns: Column<DatabaseManagementTable>[] = [
    {
      label: t('description') as string,
      Cell: (row) => <span>{t(row.label)}</span>,
    },
    {
      label: t('name') as string,
      accessor: 'value',
    },
    {
      label: t('lastUpdateFromFile') as string,
      Cell: handleStatusColumn,
    },
    {
      label: t('actions') as string,
      Cell: actionsButtons,
    },
  ];

  return (
    <ContentContainer
      title={t('databaseManagement')}
      titleAddon={generalHelpContent ? (
        <AiFillInfoCircle
          title={generalHelpTitle ?? undefined}
          onClick={() => openHelpModal(DATABASE_MANAGEMENT_FILE_GUIDE_SLUG)}
          style={{
            color: theme.colors.warning,
            cursor: 'pointer',
            fontSize: '1.05rem',
            marginTop: '0.1rem'
          }}
        />
      ) : null}
    >
      {activeHelpSlug ? (
        <HelpCenterModal
          showModal={Boolean(activeHelpSlug)}
          closeModal={closeHelpModal}
          slug={activeHelpSlug}
        />
      ) : null}

      {showDownloadModal && selectedTable && (
        <DownloadLatestExportModal
          showModal={showDownloadModal}
          closeModal={closeDownloadModal}
          table={selectedTable}
        />
      )}

      {showUpdateModal && selectedTable && (
        <UpdateFromFileModal
          showModal={showUpdateModal}
          closeModal={closeUpdateModal}
          table={selectedTable}
          onQueued={refreshTableStatuses}
        />
      )}

      {showErrorModal && selectedTable && (
        <UpdateErrorDetailsModal
          showModal={showErrorModal}
          closeModal={closeErrorModal}
          title={t(selectedTable.label)}
          errorMessage={selectedErrorMessage}
        />
      )}

      <AsyncTable
        tableName={t('databaseManagement')}
        columns={columns}
        value={tableData}
        onChange={setTableData}
        requestPage={getGenericRequestPageForAsyncTable(databaseManagementAvailableTablesApi)}
        reqListener={[updateTable]}
        options={{
          styles: {
            primaryColor: `${theme.colors.surface}`,
            secondaryColor: `${theme.colors.onSurface}`,
            alternateRowColor: theme.colors.onSurfaceLight,
            textColor: theme.colors.text,
          },
          quantityPerPageLabel: t('quantityPerPage'),
          searchPlaceholder: t('search'),
        }}
      />
    </ContentContainer>
  );
};

export default DatabaseManagementPage;
